import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useEquipmentList } from '@/lib/queries';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, ChevronRight } from 'lucide-react';
import EquipmentForm from '@/components/EquipmentForm';
import PullToRefresh from '@/components/PullToRefresh';
import { useQueryClient } from '@tanstack/react-query';

export default function Equipment() {
  const q = useEquipmentList();
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();

  return (
    <PullToRefresh onRefresh={() => queryClient.invalidateQueries({ queryKey: ['equipment'] })}>
      <div className="space-y-8">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Inventory · Equipment
            </p>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
              Equipment
            </h1>
            <p className="text-muted-foreground max-w-2xl">
              Owned equipment with model, serial, parts, and maintenance history.
            </p>
          </div>
          <Button onClick={() => setCreating(true)} className="h-11 self-start sm:self-auto" data-testid="new-equipment">
            <Plus className="h-4 w-4 mr-2" aria-hidden /> New equipment
          </Button>
        </header>

        {q.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
        {q.data && q.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No equipment yet.</p>
        ) : null}

        {q.data && q.data.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {q.data.map((e) => (
              <Link
                key={e.id}
                to={`/equipment/${e.id}`}
                className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
                data-testid={`equipment-card-${e.id}`}
              >
                <Card className="h-full transition-all border-rule group-hover:border-primary/30 group-hover:shadow-md group-hover:-translate-y-0.5">
                  <CardHeader className="space-y-1.5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {e.category}
                    </p>
                    <div className="flex items-start justify-between gap-3">
                      <CardTitle className="font-display text-lg leading-tight">{e.name}</CardTitle>
                      <ChevronRight
                        className="h-4 w-4 shrink-0 mt-1 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-transform"
                        aria-hidden
                      />
                    </div>
                    <CardDescription>
                      {[e.brand, e.model].filter(Boolean).join(' · ') || 'Unspecified make/model'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                    {e.status}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : null}

        <EquipmentForm open={creating} onOpenChange={setCreating} mode="create" />
      </div>
    </PullToRefresh>
  );
}
